// §I8 — cross-org notification badge counts.
//
// Hits the server's `/auth/me/org-notifications` endpoint, which forwards
// via HMAC to the governance `symbols-admin-ops` edge fn's
// `notifications.count_by_org` handler. Response shape:
//   { data: { counts: { [orgId]: { mentions, ticketsAssigned, meetingInvites } } } }
//
// `AppWorkspaceSwitcher`'s per-org `NotifBadge` reads `root.orgNotifCount[orgId]`
// as a single aggregate number — we sum the three signals into one badge
// count per org. The signals still live behind the flat key so a future
// per-type surfacing (e.g. mention-only pulse, ticket-only indicator)
// can read `root.orgNotifSignals[orgId]` without refetching.
//
// Fails-soft: any error leaves existing counts untouched. Server itself
// already fails-soft to `{counts:{}}` when governance URL is unset, so
// a 200 response with empty counts is normal during pre-wiring.

import { getSymbolsToken } from '../../../shared/governanceBridge.js'
import { getSDK } from '../../../shared/functions/sdk.js'

export const fetchOrgNotifCount = async (root) => {
  if (!root) return
  // Skip the poll when the tab is not visible — saves free-tier edge fn
  // invocations for users with the intranet backgrounded. The first call
  // on sign-in always goes through (document.hidden is false on active tab).
  if (typeof document !== 'undefined' && document.hidden) return
  if (!getSymbolsToken()) return
  try {
    // SDK method threads the auth header + base URL through TokenManager
    // and returns the unwrapped data payload directly.
    const data = await getSDK().getMyOrgNotifications()
    const counts = (data && data.counts) || {}
    const flat = {}
    const signals = {}
    for (const [orgId, perType] of Object.entries(counts)) {
      const m = Number(perType?.mentions || 0)
      const t = Number(perType?.ticketsAssigned || 0)
      const i = Number(perType?.meetingInvites || 0)
      flat[orgId] = m + t + i
      signals[orgId] = { mentions: m, ticketsAssigned: t, meetingInvites: i }
    }
    root.update({ orgNotifCount: flat, orgNotifSignals: signals })
  } catch {
    // fail-soft — leave prior counts intact
  }
}
